// reminders.ts — daily local-notification reminders. Two live in the app: the
// main daily "come back" reminder (seven rotating weekly notifications) and an
// optional Calm practice nudge (one daily notification). Native: repeating local notifications. Web/PWA:
// best-effort permission only (no reliable background scheduling).
import { Capacitor } from '@capacitor/core';

// 'native'  = a real repeating local notification is scheduled and will fire.
// 'web'      = saved as a preference only; NOT actually scheduled and will NOT
//              notify (no reliable background scheduling on web without a service
//              worker). Callers must treat 'web' as "saved, won't notify" and tell
//              the user the truth — never imply a notification will fire.
// 'unavailable' = could not schedule (permission denied or plugin error).
export type ScheduleResult = 'native' | 'web' | 'unavailable';

/** Fixed notification ids, one per reminder, so re-scheduling replaces cleanly. */
export const REMINDER_IDS = { programme: 4301, calm: 4302 } as const;

export interface ReminderSpec {
  id: number;
  title: string;
  body: string;
  hour: number;
  minute: number;
  /** 1 = daily, 2 = every other day, 7 = weekly, … */
  everyDays: number;
}

/** The next time hour:minute occurs (today if still ahead, else tomorrow). */
function nextOccurrence(hour: number, minute: number): Date {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute, 0, 0);
  if (d.getTime() <= now.getTime()) d.setDate(d.getDate() + 1);
  return d;
}

/** Schedule (or replace) a reminder. Daily uses a time-of-day repeat; longer
 *  intervals anchor at the next occurrence and repeat every N days. */
export async function scheduleReminder(spec: ReminderSpec): Promise<ScheduleResult> {
  const { id, title, body, hour, minute, everyDays } = spec;
  if (Capacitor.isNativePlatform()) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const perm = await LocalNotifications.requestPermissions();
      if (perm.display !== 'granted') return 'unavailable';
      await LocalNotifications.cancel({ notifications: [{ id }] });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const schedule: any =
        everyDays <= 1
          ? { on: { hour, minute }, allowWhileIdle: true, repeats: true }
          : { at: nextOccurrence(hour, minute), every: 'day', count: everyDays, allowWhileIdle: true, repeats: true };
      await LocalNotifications.schedule({ notifications: [{ id, title, body, schedule }] });
      return 'native';
    } catch {
      return 'unavailable';
    }
  }
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') await Notification.requestPermission();
  } catch {
    /* ignore */
  }
  // Web: we only persist the preference — nothing is actually scheduled and no
  // notification will fire. Return 'web' so the UI can say so honestly.
  return 'web';
}

/** Turn a reminder off. */
export async function cancelReminder(id: number): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id }] });
  } catch {
    /* ignore */
  }
}

/** The optional daily Calm practice nudge (REMINDER_IDS.calm). */
export function scheduleCalmPracticeReminder(hour: number, minute: number): Promise<ScheduleResult> {
  return scheduleReminder({
    id: REMINDER_IDS.calm,
    title: 'A moment to settle',
    body: 'Two minutes to calm your body, whenever you’re ready.',
    hour,
    minute,
    everyDays: 1,
  });
}

/** "HH:MM" → {hour, minute}, or null if malformed. */
export function parseTime(hm: string | null): { hour: number; minute: number } | null {
  if (!hm) return null;
  const m = hm.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

// ─────────────────────────────────────────────────────────────────────────────
// The daily return reminder
//
// One reminder, but SEVEN scheduled notifications: one per weekday, each repeating
// weekly, each carrying a different message. The user experiences a single daily
// nudge at their chosen time whose words change through the week, and nothing has to
// be topped up — a single repeating notification can only ever say one thing, and a
// rolling window of dated ones silently runs out for exactly the person who has not
// opened the app in a fortnight, which is the person the reminder is for.
//
// Weekday numbering is Sunday = 1 through Saturday = 7 on both platforms (Capacitor's
// Weekday enum, and java.util.Calendar.DAY_OF_WEEK on Android), so index + 1 works.

/** One id per weekday. Fixed, so re-scheduling replaces rather than duplicates. */
export const RETURN_REMINDER_IDS = [4310, 4311, 4312, 4313, 4314, 4315, 4316] as const;

/**
 * What the reminder actually says. These are the app's voice arriving on a lock
 * screen, possibly on a bad day, so every one of them has to be true whether the
 * person is in distress or simply going about their week: no streaks, no catching up,
 * no implied disappointment, and never an instruction. House style: no emoji, no em
 * dash. Seven of them, one per weekday, in the order Sunday to Saturday.
 */
export interface ReturnPrompt { title: string; body: string }
export const RETURN_PROMPTS: readonly ReturnPrompt[] = [
  { title: 'Whenever you are ready', body: 'Nothing is expected today. If now is a good moment, we are here.' },
  { title: 'However today is going', body: 'If things are hard right now, the tools are here. If they are steady, this is a good time for the slower work.' },
  { title: 'A few quiet minutes', body: 'Your programme is waiting exactly where you left it. A few minutes is plenty.' },
  { title: 'Still here for you', body: 'No catching up to do. Pick up wherever feels right.' },
  { title: 'A moment for you', body: 'Somewhere to settle if you need it, and somewhere to do the deeper work when you are ready.' },
  { title: 'We are here', body: 'Recovery is not a straight line. Whatever kind of week this has been, there is something here that can help.' },
  { title: 'Take a moment', body: 'Come back for some calm, or for the longer work. Both count.' },
];

/**
 * Ask for notification permission WITHOUT scheduling anything.
 *
 * Onboarding asks in two beats, permission then time, so the system prompt arrives
 * on the back of an explicit "yes please" rather than out of nowhere. 'unsupported'
 * means there is nothing to grant (web), which is not a failure.
 */
export async function requestNotificationPermission(): Promise<'granted' | 'denied' | 'unsupported'> {
  if (Capacitor.isNativePlatform()) {
    try {
      const { LocalNotifications } = await import('@capacitor/local-notifications');
      const perm = await LocalNotifications.requestPermissions();
      return perm.display === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') await Notification.requestPermission();
  } catch {
    /* ignore */
  }
  return 'unsupported';
}

/** Schedule (or replace) the daily return reminder at hour:minute. */
export async function scheduleDailyReturnReminders(hour: number, minute: number): Promise<ScheduleResult> {
  if (!Capacitor.isNativePlatform()) return 'web'; // saved as a preference only; nothing will fire
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== 'granted') return 'unavailable';
    await LocalNotifications.cancel({ notifications: RETURN_REMINDER_IDS.map((id) => ({ id })) });
    await LocalNotifications.schedule({
      notifications: RETURN_REMINDER_IDS.map((id, i) => ({
        id,
        title: RETURN_PROMPTS[i].title,
        body: RETURN_PROMPTS[i].body,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        schedule: { on: { weekday: (i + 1) as any, hour, minute }, allowWhileIdle: true, repeats: true },
      })),
    });
    return 'native';
  } catch {
    return 'unavailable';
  }
}

/** Turn the daily return reminder off. */
export async function cancelDailyReturnReminders(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: RETURN_REMINDER_IDS.map((id) => ({ id })) });
  } catch {
    /* ignore */
  }
}
